import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';

const STAFFING_BOARD_API = '/api/v1/staffing-board';

// ─── Types ───────────────────────────────────────────────────────────────────

export type CheckCategory =
  | 'safety'
  | 'certification'
  | 'crew'
  | 'gear_inspection'
  | 'gear_request'
  | 'communications'
  | 'project_status';

export type ProjectReadinessStatus =
  | 'ready'
  | 'warning'
  | 'blocked'
  | 'dispatched';

export interface PreflightCheck {
  level: 'ok' | 'warning' | 'blocker';
  key: string;
  category: CheckCategory;
  message: string;
  detail?: Record<string, unknown>;
}

export interface PreflightTech {
  tech_id: number;
  person_name: string;
  channel: string;
  reachable: boolean;
  blockers?: string[];
}

export interface PreflightProject {
  project_id: number;
  project_name: string;
  project_status?: string;
  project_location_name: string;
  tech_count: number;
  is_foundation?: boolean;
  checks: PreflightCheck[];
  techs: PreflightTech[];
}

export interface PreflightSummary {
  projects: number;
  techs: number;
  blockers: number;
  warnings: number;
}

export interface ExcludedProject {
  project_id: number;
  project_name: string;
  status: string;
  message: string;
}

export interface PreflightResponse {
  summary: PreflightSummary;
  projects: PreflightProject[];
  excluded_projects?: ExcludedProject[];
  confirmation_token: string;
}

export interface DispatchResult {
  dispatched: number;
  projects: number;
  skipped: { tech_id: number; reason: string }[];
  errors: { project_id: number; project_name?: string; error: string }[];
}

export type DispatchButtonState = 'empty' | 'checking' | 'ready' | 'warning' | 'blocked';

// ─── Category metadata for UI grouping ───────────────────────────────────────

export const CHECK_CATEGORY_META: Record<CheckCategory, { label: string; order: number; icon: string }> = {
  safety: { label: 'Safety', order: 0, icon: 'shield' },
  certification: { label: 'Crew & Certifications', order: 1, icon: 'award' },
  crew: { label: 'Crew & Certifications', order: 2, icon: 'users' },
  gear_inspection: { label: 'Gear & Rescue', order: 3, icon: 'wrench' },
  gear_request: { label: 'Gear & Rescue', order: 4, icon: 'package' },
  communications: { label: 'Communications', order: 5, icon: 'radio' },
  project_status: { label: 'Project Status', order: -1, icon: 'flag' },
};

/**
 * Pull the server's own message out of a failed request. Falls back to
 * `fallback` only when the server sent nothing usable.
 */
export function getDispatchErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: unknown } } | null)?.response?.data;
  if (typeof data === 'string' && data.trim() && !data.trim().startsWith('<')) return data.trim();
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    for (const key of ['error', 'detail', 'message']) {
      const v = d[key];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
  }
  return fallback;
}

export function computeProjectReadiness(
  project: PreflightProject,
  lastDispatchedAt?: string | null,
): ProjectReadinessStatus {
  if (lastDispatchedAt) return 'dispatched';
  const hasBlocker = project.checks.some((c) => c.level === 'blocker');
  if (hasBlocker) return 'blocked';
  const hasWarning = project.checks.some((c) => c.level === 'warning');
  if (hasWarning) return 'warning';
  return 'ready';
}

export function computeDispatchButtonState(
  undispatchedCount: number,
  preflight?: PreflightResponse | null,
): DispatchButtonState {
  if (undispatchedCount === 0) return 'empty';
  if (!preflight) return 'checking';
  if (preflight.summary.blockers > 0) return 'blocked';
  if (preflight.summary.warnings > 0) return 'warning';
  return 'ready';
}

export const READINESS_META: Record<ProjectReadinessStatus, { label: string; color: string; bgClass: string; textClass: string; ringClass: string }> = {
  ready: { label: 'Ready', color: 'green', bgClass: 'bg-green-50', textClass: 'text-green-700', ringClass: 'ring-green-600/20' },
  warning: { label: 'Warning', color: 'amber', bgClass: 'bg-amber-50', textClass: 'text-amber-700', ringClass: 'ring-amber-600/20' },
  blocked: { label: 'Blocked', color: 'red', bgClass: 'bg-red-50', textClass: 'text-red-700', ringClass: 'ring-red-600/20' },
  dispatched: { label: 'Dispatched', color: 'blue', bgClass: 'bg-blue-50', textClass: 'text-blue-700', ringClass: 'ring-blue-600/20' },
};

export function remediationPath(
  check: PreflightCheck,
  projectId: number,
): string | null {
  switch (check.category) {
    case 'safety':
      return `/projects/${projectId}/safety`;
    case 'certification':
      return `/crew`;
    case 'crew':
      return `/crew`;
    case 'gear_inspection':
      return `/projects/${projectId}/gear`;
    case 'gear_request':
      return `/projects/${projectId}/gear`;
    case 'communications':
      return `/crew`;
    default:
      return null;
  }
}

export function groupChecksByCategory(checks: PreflightCheck[]) {
  const groups = new Map<string, { categories: CheckCategory[]; checks: PreflightCheck[] }>();
  for (const check of checks) {
    const label = CHECK_CATEGORY_META[check.category]?.label ?? check.category;
    const existing = groups.get(label);
    if (existing) {
      existing.checks.push(check);
      if (!existing.categories.includes(check.category)) {
        existing.categories.push(check.category);
      }
    } else {
      groups.set(label, { categories: [check.category], checks: [check] });
    }
  }
  return [...groups.entries()]
    .map(([label, { categories, checks: groupChecks }]) => {
      const primaryCategory = categories.sort(
        (a, b) => (CHECK_CATEGORY_META[a]?.order ?? 99) - (CHECK_CATEGORY_META[b]?.order ?? 99),
      )[0];
      return [primaryCategory, groupChecks, label] as [CheckCategory, PreflightCheck[], string];
    })
    .sort(([a], [b]) => (CHECK_CATEGORY_META[a]?.order ?? 99) - (CHECK_CATEGORY_META[b]?.order ?? 99));
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useDispatchPreflight() {
  return useMutation<PreflightResponse, Error, { project_ids?: number[]; assignment_ids?: number[] }>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<PreflightResponse>(
        `${STAFFING_BOARD_API}/dispatch_preflight/`,
        payload,
      );
      return response.data;
    },
  });
}

export function useDispatch() {
  const qc = useQueryClient();
  return useMutation<DispatchResult, Error, { project_ids?: number[]; confirmation_token: string; override_reason?: string }>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<DispatchResult>(
        `${STAFFING_BOARD_API}/dispatch/`,
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.staffingBoard.all });
      qc.invalidateQueries({ queryKey: queryKeys.jobPackets.all });
    },
  });
}
